// =============================================================================
// notification-dialog — canonical notification/activity click→navigate mapping
// -----------------------------------------------------------------------------
// Shared by the topbar bell panel (notification-dialog.component.ts) and the
// dashboard's Recent activity feed (dashboard.component.ts) so both surfaces
// route a click the same way, without a divergent copy of the logic.
//
// A notification does NOT carry a usable phase: `Notification.projectPhase` is
// @Deprecated and never populated, so we resolve where a project CURRENTLY
// lives from the project feed instead, keyed on the notification's `siteId`.
// We look up the site's live per-site proposal `status` in the same feed the
// list pages consume, then route to whichever list/workspace that status maps
// to — mirroring the work map pin's "View project" (navigate to the list and
// pass `scrollToProjectId` so the list scrolls to + flashes the
// `#project-<id>` card). Pure function, no Angular dependency — the dialog
// additionally closes itself when `closesDialog` is set (a dialog-only
// concern), which is why it is returned alongside the route rather than baked
// into a `router.navigate()` call here.
//
// The status → page buckets below are duplicated (with source citations) from
// the three list pages' own `fetchData()` filters rather than refactored into
// a shared module; keep them in lockstep with those pages.
//
// A notification WITHOUT a `siteId` but WITH a `projectId` is resolved at the
// project level instead — see `resolveNotificationRoute`. That shape is raised
// by the bulk-push failure notice (`createNotificationForFailedBidsTransmission`),
// which deliberately consolidates N per-product failures into one project-level
// notice and so has no single site to key on.
// =============================================================================

import { sanitizeProjectDetails } from '../list/list-grouping.util';

export interface ResolvedNotificationRoute {
  /** Commands to pass to `Router.navigate()`. */
  commands: string[];
  /** Whether the notification-dialog panel should close after navigating. */
  closesDialog: boolean;
  /**
   * Router navigation state to forward — mirrors the work map's "View project"
   * pin:
   *  - Invitations/Proposals: `scrollToProjectId` makes the list scroll to +
   *    flash the `#project-<id>` card.
   *  - Active: `workspaceBackType: 'dashboard'` so the workspace's "Back to
   *    Active" button routes to the `/active` list (the workspace has no Active
   *    list in its history to pop back to — `location.back()` would otherwise
   *    land on whatever surface launched the notification, e.g. the Dashboard).
   *    Same origin token the map pin stamps; see `workspace.component.ts back()`.
   */
  state?: { scrollToProjectId?: string; workspaceBackType?: string };
}

// Invitations page — mirrors invitations-page.component.ts's fetchData filter
// (Task B4 added COUNTERED — a countered proposal is still an open
// opportunity, so a notification about it must still route somewhere rather
// than silently no-op; the staffing counter-back adds RE_OFFERED, which is
// the status the counter-back notification itself is raised against — if it
// did not route, the notification telling the PA staffing answered would go
// nowhere).
const INVITATION_STATUSES = new Set<string>([
  'INVITATION',
  'OFFERED',
  'COUNTERED',
  'RE_OFFERED',
]);

// Proposals ("Pending") page — mirrors proposals-page.component.ts's filter.
const PROPOSAL_STATUSES = new Set<string>([
  'SUBMITTED',
  'PROPOSAL',
  'BID_LOST',
  'NO_RESPONSE',
  'BID_WON',
]);

// Active workspace — mirrors active-page.component.ts's fetchData filter.
const ACTIVE_STATUSES = new Set<string>([
  'ACTIVE',
  'REVIEW_PENDING',
  'REVISION_REQUESTED',
  'REVISION_PENDING',
]);

// proposals-page.component.ts applies a SECOND, project-level filter on top of
// the per-site PROPOSAL_STATUSES: it drops any project whose (bucket-filtered)
// sites are ALL BID_LOST / NO_RESPONSE. So a terminal-status site only renders
// there when at least one sibling site is still live/won. Mirror that here so a
// fully-lost project's notification doesn't route to a list that hides it.
const PROPOSAL_TERMINAL_STATUSES = new Set<string>(['BID_LOST', 'NO_RESPONSE']);
const PROPOSAL_SURVIVING_STATUSES = new Set<string>([
  'SUBMITTED',
  'PROPOSAL',
  'BID_WON',
]);

/**
 * True when the owning project still surfaces on `/proposals` — i.e. at least
 * one of its sites has a non-terminal proposals-bucket status. Mirrors the
 * proposals page's project-level exclusion.
 */
function projectSurvivesProposalsFilter(
  siblingStatuses: (string | undefined)[]
): boolean {
  return siblingStatuses.some(
    (s) => !!s && PROPOSAL_SURVIVING_STATUSES.has(s)
  );
}

/**
 * Locate the notification's site in the project feed and return its live
 * per-site proposal `status` plus the owning project's `projectId`. Returns
 * `null` when the notification has no `siteId`, or the site isn't present in
 * the feed (e.g. a completed project that's dropped off the active feed, or
 * the feed hasn't loaded yet). Pure — safe to call from either surface.
 */
function resolveSiteLocation(
  notification: any,
  projects: any[]
): {
  status: string | undefined;
  projectId: string | undefined;
  siblingStatuses: (string | undefined)[];
} | null {
  const siteId = notification?.siteId;
  if (!siteId) {
    return null;
  }
  for (const parent of sanitizeProjectDetails(projects)) {
    for (const detail of parent.sites) {
      if (detail?.site?.id === siteId) {
        return {
          status: detail?.proposal?.status,
          projectId: parent?.projectId,
          // Every sibling site's status in the SAME project — the proposals
          // route needs these to mirror the page's project-level exclusion.
          siblingStatuses: (parent.sites ?? []).map(
            (d: any) => d?.proposal?.status
          ),
        };
      }
    }
  }
  return null;
}

/**
 * Project-level destination for a notification that names a project but no
 * site — currently the consolidated bulk-push failure notice, which reports N
 * failed products as one message and therefore has no single site to key on.
 *
 * `/invitations` and not a lookup, because the destination is already
 * determined by what a failed push DOES: both failure paths revert the
 * products to a status that lives on that list. A failed submit goes back to
 * INVITATION; a failed accept goes back to its captured
 * `statusBeforeSubmission` — OFFERED or RE_OFFERED. All of
 * INVITATION/OFFERED/COUNTERED/RE_OFFERED are in `INVITATION_STATUSES`, so
 * every product the notice is about is on the Invitations list by the time the
 * vendor can click it. That makes this deterministic without consulting the
 * feed, which matters because the feed may not have re-emitted the reverted
 * statuses yet when the notice arrives.
 *
 * Carries the same `scrollToProjectId` state the site-keyed Invitations route
 * and the work map's "View project" pin pass, so the list scrolls to and
 * flashes the `#project-<id>` card with the reverted products sitting in it,
 * ready to resubmit.
 */
function resolveProjectRoute(projectId: string): ResolvedNotificationRoute {
  return {
    commands: ['/invitations'],
    closesDialog: true,
    state: { scrollToProjectId: projectId },
  };
}

/**
 * Resolve where a notification (topbar panel entry or dashboard activity row)
 * should navigate to, from the project feed keyed on `siteId`:
 *  - status in the Invitations set → `/invitations`, scroll to the card.
 *  - status in the Proposals set   → `/proposals` (the "Pending" page), scroll —
 *    EXCEPT a BID_LOST/NO_RESPONSE site whose project is fully terminal, which
 *    the proposals page hides (see `projectSurvivesProposalsFilter`) → no-op.
 *  - status in the Active set       → `/active/:siteId` workspace deep-link
 *    (the workspace ignores `scrollToProjectId`; it is passed
 *    `workspaceBackType: 'dashboard'` so "Back to Active" returns to the
 *    `/active` list rather than the launching surface — mirrors the map pin).
 *
 * `state.scrollToProjectId` uses the resolved `projectId` (falling back to the
 * notification's own `projectId` if the feed row lacks one).
 *
 * When the notification carries NO `siteId` at all but does carry a
 * `projectId`, it is resolved at the PROJECT level to `/invitations` with the
 * same scroll state — see `resolveProjectRoute`.
 *
 * Returns `null` (a no-op — no navigation, no error) for ALL of: neither
 * `siteId` nor `projectId`; the site isn't found in the feed; the resolved
 * status maps to none of the three sets; or an empty/absent `projects` feed
 * (for a site-keyed notification). We do NOT fall back to the Dashboard.
 */
export function resolveNotificationRoute(
  notification: any,
  projects: any[]
): ResolvedNotificationRoute | null {
  const location = resolveSiteLocation(notification, projects);
  if (!location) {
    // No site to key on. A project-level notification still has a
    // deterministic destination; anything else stays a no-op. Gated on the
    // ABSENCE of `siteId` rather than on `location` being null, so a
    // site-keyed notification whose site is missing from the feed (or whose
    // status maps to no list) keeps its existing no-op contract instead of
    // silently landing on /invitations.
    if (!notification?.siteId && notification?.projectId) {
      return resolveProjectRoute(notification.projectId);
    }
    return null;
  }

  const { status } = location;
  const scrollToProjectId = location.projectId ?? notification?.projectId;

  if (status && INVITATION_STATUSES.has(status)) {
    return {
      commands: ['/invitations'],
      closesDialog: true,
      state: { scrollToProjectId },
    };
  }
  if (status && PROPOSAL_STATUSES.has(status)) {
    // A BID_LOST / NO_RESPONSE site only renders on /proposals when its project
    // survives the page's project-level filter (a live/won sibling). Otherwise
    // the project is hidden there — no-op rather than land on a list without it.
    if (
      PROPOSAL_TERMINAL_STATUSES.has(status) &&
      !projectSurvivesProposalsFilter(location.siblingStatuses)
    ) {
      return null;
    }
    return {
      commands: ['/proposals'],
      closesDialog: true,
      state: { scrollToProjectId },
    };
  }
  if (status && ACTIVE_STATUSES.has(status)) {
    return {
      commands: ['/active', notification.siteId],
      closesDialog: true,
      state: { workspaceBackType: 'dashboard' },
    };
  }

  return null;
}
